import { Search, Settings, Stethoscope, Wrench, ArrowRight, Clock, Shield, CheckCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const services = [
  {
    icon: Search,
    title: 'Purchase Inspection',
    features: [
      'OBD Scan',
      'Detailed Reports', 
      'Buy With Confidence',
      'Estimate Repairs',
      'Vehicle Historical',
      'Recommendations'
    ],
    link: '/services/pre-purchase-inspection'
  },
  {
    icon: Settings,
    title: 'Car Servicing',
    features: [
      'Quality Oils',
      'Filter Replacement',
      'Fluid Top-ups', 
      'Warranty Protection',
      'Genuine Parts',
      'Digital Service Records'
    ],
    link: '/services/car-servicing'
  },
  {
    icon: Stethoscope,
    title: 'Diagnostics',
    features: [
      'Professional Analysis',
      'Modern Equipment',
      'Clear Explanation',
      'OBD Code Reading',
      'Visual Diagnostic',
      'Detailed Report'
    ],
    link: '/services/diagnostics'
  },
  {
    icon: Wrench,
    title: 'Mobile Repairs',
    features: [
      'Safety First',
      'Quality Parts',
      'Warranty',
      'Brake Repairs',
      'Battery Replacement',
      'Much More'
    ],
    link: '/services/mobile-repairs'
  }
];

export const ServicesSection = () => {
  const navigate = useNavigate();

  return (
    <section id="services" className="py-12 lg:py-24 bg-gradient-to-br from-background via-muted/10 to-primary/5 relative overflow-hidden mobile-section-spacing">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 hero-pattern opacity-20"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="section-header">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-primary-hover rounded-2xl mb-8 shadow-[var(--shadow-glow)]">
            <Settings className="w-10 h-10 text-white" />
          </div>
          <h2 className="section-title">
            Professional Mobile Services
          </h2>
          <p className="section-subtitle">
            Comprehensive automotive services delivered directly to your location with professional equipment and certified mechanics.
          </p>
          <div className="section-divider" />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-12 mobile-grid-fix">
          {services.map((service, index) => (
            <Link key={index} to={service.link} className="block">
              <div className="service-card group flex flex-col h-full text-center cursor-pointer mobile-text-center">
                <div className="mb-6 sm:mb-8">
                  <div className="section-icon mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-all duration-300">
                    <service.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-tight px-2 whitespace-nowrap mobile-text-center">
                    {service.title}
                  </h3>
                </div>
                
                <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-1 text-left">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start text-muted-foreground">
                      <div className="w-3 h-3 bg-primary rounded-full mr-3 sm:mr-4 mt-1.5 flex-shrink-0 shadow-sm" />
                      <span className="leading-relaxed font-medium text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-auto pt-4 sm:pt-6 border-t border-border/30 text-center mobile-text-center">
                  <span className="inline-flex items-center text-primary hover:text-primary-hover font-bold transition-colors duration-300 group/link text-sm sm:text-base">
                    About This Service
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover/link:translate-x-1 transition-transform duration-300" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        <div className="mt-20">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl p-12 border border-primary/10 shadow-[var(--shadow-elegant)]">
            <div className="text-center space-y-8">
              <h3 className="text-3xl md:text-4xl font-bold text-foreground">
                Your Car Deserves Professional Service
              </h3>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Don't compromise on quality. Get professional mobile mechanic services with transparent pricing and guaranteed satisfaction.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button 
                  onClick={() => navigate('/book-now')}
                  className="btn-premium text-lg py-4 px-10"
                >
                  BOOK SERVICE NOW
                </button>
                <button 
                  onClick={() => navigate('/get-quote')}
                  className="btn-outline-premium text-lg py-4 px-10"
                >
                  Get Your Free Quote
                </button>
              </div>
              
              {/* Trust indicators */}
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 pt-8 mt-4">
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-primary" />
                  <span className="text-foreground font-semibold">Warranty</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary" />
                  <span className="text-foreground font-semibold">Licensed & Insured</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};